/**
 * Хуки данных гостевого раздела «Моё проживание» (Tanstack Query).
 *
 * Источники:
 *   useMyStays            — GET /v1/me/stays (проживания гостя, ownership на бэке)
 *   useRoomDetail         — GET /v1/rooms/{id} (type_id/number) + публичный
 *                           каталог GET /v1/public/{slug}/room-types для
 *                           фото/удобств/типа (детали номера, design §3)
 *   useRequestExtension   — POST /v1/stays/{id}/extensions (продление)
 *   useRequestLateCheckout— POST /v1/stays/{id}/late-checkout (поздний выезд)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkOutMyStay,
  getMyStays,
  getPublicRoomTypes,
  getRoom,
  requestStayExtension,
  requestStayLateCheckout,
} from '@hsc/api-client';
import type {
  RequestLateCheckoutInput,
  RequestStayExtensionInput,
} from '@hsc/api-client';
import type {
  PublicRoomType,
  RoomView,
  StayExtensionResponse,
  StayLateCheckoutResponse,
  StayResponse,
} from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';

/** GET /v1/me/stays — проживания текущего гостя. */
export function useMyStays() {
  return useQuery<StayResponse[]>({
    queryKey: ['me', 'stays'],
    queryFn: () => getMyStays(),
  });
}

/** Детали номера гостя: тип/удобства/фото из публичного каталога по type_id. */
export interface RoomDetail {
  room: RoomView;
  type?: PublicRoomType | undefined;
}

export function useRoomDetail(roomId: string | null) {
  return useQuery<RoomDetail>({
    queryKey: ['me', 'room-detail', roomId],
    enabled: Boolean(roomId),
    queryFn: async () => {
      const room = await getRoom(roomId as string);
      // Публичный каталог несёт фото/удобства/имя типа — сопоставляем по id.
      const slug = resolveTenantSlug();
      let type: PublicRoomType | undefined;
      if (slug !== null && slug !== '') {
        try {
          const cat = await getPublicRoomTypes(slug);
          type = cat.room_types.find((t) => t.id === room.type_id);
        } catch {
          // публичный каталог недоступен — покажем номер без обогащения
          type = undefined;
        }
      }
      return { room, type };
    },
  });
}

/** POST /v1/stays/{id}/extensions — запрос продления. */
export function useRequestExtension() {
  const qc = useQueryClient();
  return useMutation<
    StayExtensionResponse,
    Error,
    { stayId: string; input: RequestStayExtensionInput }
  >({
    mutationFn: ({ stayId, input }) => requestStayExtension(stayId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'stays'] });
      void qc.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}

/** POST /v1/me/stays/{id}/check-out — гость инициирует выселение «Выселиться». */
export function useSelfCheckout() {
  const qc = useQueryClient();
  return useMutation<StayResponse, Error, { stayId: string }>({
    mutationFn: ({ stayId }) => checkOutMyStay(stayId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'stays'] });
    },
  });
}

/** POST /v1/stays/{id}/late-checkout — запрос позднего выезда. */
export function useRequestLateCheckout() {
  const qc = useQueryClient();
  return useMutation<
    StayLateCheckoutResponse,
    Error,
    { stayId: string; input: RequestLateCheckoutInput }
  >({
    mutationFn: ({ stayId, input }) => requestStayLateCheckout(stayId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me', 'stays'] });
      void qc.invalidateQueries({ queryKey: ['requests'] });
    },
  });
}
